import React, { useRef, useEffect, useMemo } from 'react';
import { FormContextValues } from 'react-hook-form';

import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonIcon from '@material-ui/icons/Person';

import FormPeople from '../../types/FormPeople';

const useStyles = makeStyles(theme => ({
  image: {
    position: 'relative',
    width: '100%',
    paddingTop: '100%',
    '&:hover, &$focusVisible': {
      zIndex: 1,
      '& $imageBackdrop': {
        opacity: 0.4,
      },
      '& $imageTitle': {
        opacity: 1,
        border: '4px solid currentColor',
      },
      '& $noPhotoIconContainer': {
        opacity: 0,
      },
    },
  },
  paperImage: {
    backgroundColor: '#ccc',
  },
  focusVisible: {},
  inputFile: {
    display: 'none',
  },
  imageButton: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
  },
  imageSrc: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center 40%',
    borderRadius: 4,
  },
  imageBackdrop: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.palette.common.black,
    opacity: 0,
    transition: theme.transitions.create('opacity'),
    borderRadius: 4,
  },
  imageTitle: {
    position: 'relative',
    opacity: 0,
    padding: `${theme.spacing(2)}px ${theme.spacing(4)}px ${theme.spacing(1) + 6}px`,
  },
  containerButtons: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
  },
  noPhotoIconContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#efefef',
    opacity: 1,
    transition: theme.transitions.create('opacity'),
  },
  noPhotoIcon: {
    fontSize: 64,
  },
}));

interface Props {
  form: FormContextValues<FormPeople>;
}

export default function PeopleImg({ form }: Props) {
  const classes = useStyles();
  const refInputImage = useRef<HTMLInputElement>(null);
  const image = form.watch('image');
  const refOldImageURL = useRef<string | null>();
  const imageURL = useMemo<string | null>(() => {
    if (typeof image === 'string') {
      return image;
    }
    if (refOldImageURL.current) {
      URL.revokeObjectURL(refOldImageURL.current);
    }
    const newImageURL = image && URL.createObjectURL(image);
    refOldImageURL.current = newImageURL;
    return newImageURL;
  }, [ image ]);

  useEffect(() => {
    form.register({ name: 'image' });
    return () => {
      if (refOldImageURL.current) {
        URL.revokeObjectURL(refOldImageURL.current);
      }
    };
  }, [ form ]);

  const onClickImage = () => {
    setTimeout(() => {
      if (refInputImage.current) {
        refInputImage.current.value = '';
        refInputImage.current.click();
      }
    }, 250);
  };

  const onChangeImage = (e: any) => {
    if (!e.target.files || !e.target.files[0]) {
      return;
    }
    if (e.target.files[0].size > 2 * 1024 * 1024) {
      alert('Tamanho da imagem excedido. Selecione uma imagem menor que 2MB.');
      return;
    }
    form.setValue('image', e.target.files[0]);
  };

  const onClickRemoveImage = () => {
    form.setValue('image', null);
  };

  return (
    <>
      <input
        ref={refInputImage}
        name='image'
        accept='image/*'
        className={classes.inputFile}
        type='file'
        onChange={onChangeImage}
      />
      <Paper elevation={1} className={classes.paperImage}>
        <ButtonBase
          focusRipple
          className={classes.image}
          focusVisibleClassName={classes.focusVisible}
          onClick={onClickImage}
        >
          <span
            className={classes.imageSrc}
            style={{
              backgroundImage: `url(${imageURL})`,
            }}
          />
          <span className={classes.imageBackdrop} />
          {!imageURL && (
            <span className={classes.noPhotoIconContainer}>
              <PersonIcon className={classes.noPhotoIcon} />
            </span>
          )}
          <span className={classes.imageButton}>
            <Typography
              className={classes.imageTitle}
              component='span'
              variant='subtitle1'
              color='inherit'
              children={imageURL ? 'Alterar' : 'Adicionar'}
            />
          </span>
        </ButtonBase>
      </Paper>
      <div className={classes.containerButtons}>
        <Tooltip title='Adicionar imagem'>
          <IconButton
            onClick={onClickImage}
          >
            <AddAPhotoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Remover imagem'>
          <div>
            <IconButton
              onClick={onClickRemoveImage}
              disabled={!imageURL}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        </Tooltip>
      </div>
    </>
  );
}
